/**
 * Type definitions for Bookmarks store
 * 
 * This file provides TypeScript interfaces for bookmark-related data
 * to ensure consistent typing throughout the application.
 */

/**
 * Raw bookmark data as stored in the database
 */
export interface BookmarkData {
  id: string;
  topic_id: string;
  user_id: string;
  url: string;
  title: string | null;
  description: string | null;
  image_url: string | null;
  summary: string | null;
  note: string | null;
  created_at: string;
  updated_at?: string;
  metadata?: {
    fetched?: boolean;
    error?: string;
    processing_status?: 'pending' | 'processing' | 'completed' | 'failed';
    ai_summarized?: boolean;
    screenshot_taken?: boolean;
    client_id?: string;
    last_update?: string;
    [key: string]: any;
  };
  user_info?: {
    name?: string;
    email?: string;
    avatar_url?: string;
  };
}

/**
 * Formatted bookmark for use in the UI
 */
export interface FormattedBookmark {
  id: string;
  topicId: string;
  userId: string;
  url: string;
  title: string | null;
  description: string | null;
  imageUrl: string | null;
  summary: string | null;
  note: string | null;
  createdAt: string;
  updatedAt?: string;
  metadata: {
    fetched: boolean;
    error?: string;
    processingStatus: 'pending' | 'processing' | 'completed' | 'failed';
    aiSummarized?: boolean;
    screenshotTaken?: boolean;
  };
  userInfo?: {
    name: string | null;
    email: string | null;
    avatarUrl: string | null;
  };
}

/**
 * Input for creating a new bookmark
 */
export interface CreateBookmarkInput {
  topicId: string;
  url: string;
}

/**
 * Input for updating an existing bookmark
 */
export interface UpdateBookmarkInput {
  title?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  summary?: string | null;
  note?: string | null;
  metadata?: Partial<{
    fetched: boolean;
    error: string;
    processingStatus: 'pending' | 'processing' | 'completed' | 'failed';
    aiSummarized: boolean;
    screenshotTaken: boolean;
  }>;
}

/**
 * Bookmark filter options
 */
export interface BookmarkFilters {
  searchQuery?: string;
  userIds?: string[];
  // Additional filter options
  domains?: string[];
  dateRange?: {
    from?: string;
    to?: string;
  };
  processingStatus?: 'pending' | 'processing' | 'completed' | 'failed';
}

/**
 * Helper function to format a bookmark for consumption in UI components
 */
export function formatBookmark(bookmark: BookmarkData): FormattedBookmark {
  return {
    id: bookmark.id,
    topicId: bookmark.topic_id,
    userId: bookmark.user_id,
    url: bookmark.url,
    title: bookmark.title,
    description: bookmark.description,
    imageUrl: bookmark.image_url,
    summary: bookmark.summary,
    note: bookmark.note,
    createdAt: bookmark.created_at,
    updatedAt: bookmark.updated_at,
    metadata: {
      fetched: bookmark.metadata?.fetched || false,
      error: bookmark.metadata?.error,
      processingStatus: bookmark.metadata?.processing_status || 'pending',
      aiSummarized: bookmark.metadata?.ai_summarized,
      screenshotTaken: bookmark.metadata?.screenshot_taken,
    },
    userInfo: bookmark.user_info ? {
      name: bookmark.user_info.name || null,
      email: bookmark.user_info.email || null,
      avatarUrl: bookmark.user_info.avatar_url || null
    } : undefined
  };
}

/**
 * Helper function to format a bookmark for sending to the database
 */
export function formatBookmarkForDatabase(bookmark: FormattedBookmark): BookmarkData {
  return {
    id: bookmark.id,
    topic_id: bookmark.topicId,
    user_id: bookmark.userId,
    url: bookmark.url,
    title: bookmark.title,
    description: bookmark.description,
    image_url: bookmark.imageUrl,
    summary: bookmark.summary,
    note: bookmark.note,
    created_at: bookmark.createdAt,
    updated_at: bookmark.updatedAt,
    metadata: {
      fetched: bookmark.metadata.fetched,
      error: bookmark.metadata.error,
      processing_status: bookmark.metadata.processingStatus,
      ai_summarized: bookmark.metadata.aiSummarized,
      screenshot_taken: bookmark.metadata.screenshotTaken
    },
    user_info: bookmark.userInfo ? {
      name: bookmark.userInfo.name || undefined,
      email: bookmark.userInfo.email || undefined,
      avatar_url: bookmark.userInfo.avatarUrl || undefined
    } : undefined
  };
}